import React, { useEffect } from 'react';
import Banner from '~/components/Banner';
import EditAddress from '~/components/EditAddress';
import Stopwatch from '~/components/Icons/outline/Stopwatch';
import PageHeader from '~/components/PageHeader';
import { PAGE_TITLE } from '~/constants/products';
import { Contact } from '~/types/contact-credit';
import { normalizeName } from '~/helpers/normalizeName';
import Portal from '~/components/Portal';
import GDTBanner from '~/components/GDTBanner';
import { iconMap } from '~/constants/iconMap';
import PhoneNumberHeader from '~/components/PhoneNumber/PhoneNumberHeader';
import useMediaQuery from '~/hooks/useMediaQuery';
import HeaderBanner from '~/components/Banner/HeaderBanner';
import { Product } from '~/types/products';
import { setHideFrontierLogo } from '~/redux/actions/common';
import { useDispatch } from 'react-redux';

interface Props {
  isCopper: boolean;
  mobile?: boolean;
  xsMobile?: boolean;
  bannerText: string;
  title?: string;
  subtitle?: {
    copy: string;
    enabled?: string;
  };
  ltoBanner?: {
    enabled: string;
    icon: string;
    emphasized: string;
    copy: string;
    tooltip?: string;
    portal?: string;
    disclaimer?: string;
    displayStyle?: string;
    iconColor?: string;
    backgroundColor?: string;
    useBackgroundTheme?: string;
    backgroundTheme?: string;
    legalKey?: string;
    netflixToggleLink?: {
      enabled?: string;
      text?: string;
    };
  };
  gdtBanner?: {
    enabled?: string;
    title?: string;
    subtitle?: string;
    backgroundColor?: string;
    button?: {
      text: string;
      backgroundColor: string;
      textColor: string;
    };
    delay?: {
      enabled: string;
      time: string;
    };
    sticky?: string;
  };
  contact?: Contact;
  isCopper2Fiber: boolean;
  landingPageProduct?: string;
  landingPageProductAvailable?: boolean;
  phoneNumberHeaderEnabled?: boolean;
  setVasModal?: (product: Product) => void;
  headerBanner?: {
    enabled: string;
    icon: string;
    text: string;
    subText: string;
    backgroundColor: string;
    button?: {
      text: string;
    };
  };
  setClickToToggleNetflixBundle?: (value: boolean) => void;
}

const getTitles = ({
  isCopper2Fiber,
  isCopper,
  title,
  subtitle,
  contact,
  landingPageProduct,
  landingPageProductAvailable,
}: Omit<Props, 'bannerText' | 'ltoBanner'>) => {
  const titles = {
    title: '',
    subtitle: '',
  };
  // Show specific copy when product is selected on landing page and user is serviceable for it
  if (landingPageProductAvailable) {
    titles.title = 'Good news! The plan you selected is available.';
    titles.subtitle = "Your home is in a fiber-optic service area. You're eligible for our fast fiber plans.";
  } else if (landingPageProduct && !landingPageProductAvailable) {
    // Handles users who selected a fiber product on LP but are copper serviceable
    titles.title = PAGE_TITLE.COPPER.title;
    titles.subtitle =
      "Based on your address, the Fiber plan you selected is not currently available in your area, but we've got another plan for you.";
  } else if (isCopper2Fiber) {
    const fullName = `${normalizeName(contact?.firstname)} ${normalizeName(contact?.lastname)}`.trim();
    const comma = fullName ? ',' : '';
    const name = `${comma} ${fullName}`.trim();
    titles.title = `Hey${name}! Good news, you're eligible for fiber internet!`;
    titles.subtitle = `We've expanded fiber-optic service to your area. Don't miss out on fast and reliable speed!`;
  } else {
    if (isCopper) {
      titles.title = PAGE_TITLE.COPPER.title;
      titles.subtitle = PAGE_TITLE.COPPER.subtitle;
    } else {
      titles.title = title || PAGE_TITLE.FIBER.title;
      if (subtitle?.enabled !== 'false') titles.subtitle = subtitle?.copy || PAGE_TITLE.FIBER.subtitle;
    }
  }

  return titles;
};

const getBannerText = ({
  isCopper,
  bannerText,
  ltoBanner,
}: Pick<Props, 'bannerText' | 'ltoBanner' | 'isCopper'>): string => {
  if (isCopper) return bannerText;
  return `<b>${ltoBanner?.emphasized}</b><br/>${ltoBanner?.copy}`;
};

const PlanPackageHeader: React.FC<Props> = ({
  isCopper,
  mobile,
  bannerText,
  phoneNumberHeaderEnabled,
  xsMobile,
  ltoBanner,
  gdtBanner,
  headerBanner,
  setVasModal,
  setClickToToggleNetflixBundle,
  ...rest
}) => {
  const { title, subtitle } = getTitles({ isCopper, ...rest });
  const Icon = iconMap[ltoBanner?.icon] || Stopwatch;
  const isHeader = ltoBanner?.portal !== 'true' || !mobile;
  const gdtBannerEnabled = gdtBanner?.enabled === 'true' && mobile;
  const isMobile = useMediaQuery('(max-width: 767px)');

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setHideFrontierLogo(headerBanner?.enabled === 'true'));
  }, [headerBanner, dispatch]);

  return (
    <>
      {headerBanner?.enabled === 'true' && (
        <HeaderBanner
          metadata={headerBanner}
          setVasModal={setVasModal}
          setClickToToggleNetflixBundle={setClickToToggleNetflixBundle}
        />
      )}
      {gdtBannerEnabled && (
        <GDTBanner
          enabled="true"
          title={gdtBanner?.title}
          subtitle={gdtBanner?.subtitle}
          backgroundColor={gdtBanner?.backgroundColor}
          button={gdtBanner?.button}
          delay={gdtBanner?.delay}
          sticky={gdtBanner?.sticky}
        />
      )}
      <PageHeader headingLevel={isCopper ? 2 : 1} title={title} subtitle={{ text: subtitle }}>
        {(mobile || xsMobile) && <EditAddress />}
        <div className="portal-container">
          <div id="ltoBannerHeader" />
          <EditAddress />
        </div>
        <Portal portalId={isHeader ? '#ltoBannerHeader' : '#ltoBannerPortal'} active={ltoBanner?.enabled === 'true'}>
          {isMobile && phoneNumberHeaderEnabled && <PhoneNumberHeader />}
          <Banner
            iconColor={ltoBanner?.iconColor}
            iconBg={ltoBanner?.backgroundColor}
            useBackgroundTheme={ltoBanner?.useBackgroundTheme}
            backgroundTheme={ltoBanner?.backgroundTheme}
            text={getBannerText({ isCopper, bannerText, ltoBanner })}
            icon={<Icon className="banner__icon" />}
            iconName={ltoBanner?.icon}
            legalKey={ltoBanner?.legalKey}
            netflixToggleLink={ltoBanner?.netflixToggleLink}
            setClickToToggleNetflixBundle={setClickToToggleNetflixBundle}
            {...(!['N/A', 'none'].includes(ltoBanner?.tooltip) && { tooltip: ltoBanner?.tooltip })}
            {...(ltoBanner?.displayStyle && { displayStyle: ltoBanner?.displayStyle })}
          >
            {ltoBanner?.disclaimer !== 'none' && <small className="banner__disclaimer">{ltoBanner?.disclaimer}</small>}
          </Banner>
          {!isMobile && phoneNumberHeaderEnabled && <PhoneNumberHeader />}
        </Portal>
      </PageHeader>
    </>
  );
};

PlanPackageHeader.displayName = 'PlanPackageHeader';

export default PlanPackageHeader;
